import React, { useEffect, useMemo, useState } from 'react'
import { Row, Col, Spinner, Button, Form, Container } from 'react-bootstrap'
import useFetch from '../../../_fetch'
import { ClickDataTable } from './click-table'
import { formatNumberLocale, formatNumber, toNumber } from "../../../_utils"
import { useAuth } from '../../../auth/auth-context'
import { ClicksPerCampaign,ClicksPerLink,ClicksPerPath,IPublisher,Link,Infos,ITransaction, ImpressionsPerPath, FactRow } from './types'

const LinkScriptDashboard: React.FC<{publishers: IPublisher[]}> = ({ publishers }) => {
    const { doFetch } = useFetch()
    const { user } = useAuth()

    const [infos, setInfos] = useState<Infos & { linkObj: {[key: string]: Link} }>({ domains: [], paths: [], links: [], linkObj: {} })

    const [clicksPerLink, setClicksPerLink] = useState<ClicksPerLink[]>([])
    const [clicksPerPath, setClicksPerPath] = useState<ClicksPerPath[]>([])
    const [clicksPerCampaign, setClicksPerCampaign] = useState<ClicksPerCampaign[]>([])

    const [badClicksPerLink, setBadClicksPerLink] = useState<ClicksPerLink[]>([])
    const [badClicksPerPath, setBadClicksPerPath] = useState<ClicksPerPath[]>([])

    const [impressionsPerPath, setImpressionsPerPath] = useState<ImpressionsPerPath[]>([])

    const [rawTransactions, setRawTransactions] = useState<any[]>([]);
    

    const formatTransactions = (transactions: {ref:string, totalTransactions: number, totalCommission: number}[]) => {
        const formatted:ITransaction[] = []
        const regex = /^n(\d+)n(\d+)n(\d+)n([A-Za-z0-9]+)$/

        for(const transaction of transactions) {
            const match = (transaction.ref || '').match(regex)

            if(!match) continue

            const [_full, publisherInternalIdStr, domainIdStr, pathIdStr, urlHash] = match
    
            const publisherInternalId = toNumber(publisherInternalIdStr)
            const pathId = toNumber(pathIdStr)
            const domainId = toNumber(domainIdStr)
            const urlId = infos.linkObj[urlHash]?.id
    
            if(!publisherInternalId || !domainId || !pathId || !urlId) continue
    
            formatted.push({
                publisherInternalId,
                domainId,
                pathId,
                urlId,
                totalTransactions: transaction.totalTransactions,
                totalCommission: transaction.totalCommission
            })
        }
        return formatted
    }
    const transactions = useMemo(() => {
        return formatTransactions(rawTransactions)
    }, [rawTransactions, infos.linkObj]) || []

    const [loading, setLoading] = useState(true)

    const [fromDate, setFromDate] = useState(() => {
        const firstDay = new Date()
        firstDay.setDate(1)
        return firstDay.toISOString().substring(0, 10)
    })

    const [toDate, setToDate] = useState(() => new Date().toISOString().substring(0, 10))

    const fetchClicks = async (from?: string, to?: string) => {
        setLoading(true)
        try {
            const finalFrom = new Date(from || fromDate)
            finalFrom.setHours(0, 0, 0, 0)
            const finalTo = new Date(to || toDate)
            finalTo.setHours(23, 59, 59, 999)

            const resClicks = await doFetch('/byelink/aggregated-clicks', {
                fromDate: finalFrom.toISOString(),
                toDate: finalTo.toISOString()
            })

            if (resClicks.status >= 400) {
                throw await resClicks.text()
            }

            const clicksJson = await resClicks.json()

            setRawTransactions(clicksJson.transactions)
            // Tracked
            setClicksPerLink(clicksJson.clicks.clicksPerLink)
            setClicksPerPath(clicksJson.clicks.clicksPerPath)
            setClicksPerCampaign(clicksJson.clicks.clicksPerCampaign)

            // Untracked (bad)
            setBadClicksPerLink(clicksJson.badClicks.clicksPerLink)
            setBadClicksPerPath(clicksJson.badClicks.clicksPerPath)

            setImpressionsPerPath(clicksJson.impressions)

            formatTransactions(clicksJson.transactions)

        } catch (err) {
            console.error('Error fetching clicks:', err)
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        const fetchData = async () => {
            try {
                const resInfos = await doFetch('/byelink/infos')
                if (resInfos.status >= 400) throw await resInfos.text()
                
                const infosRaw:Infos = await resInfos.json()
                const linkObj: {[key: string]: Link} = {}
                infosRaw.links.forEach(link => {
                    linkObj[link.hash] = link
                })
                setInfos({domains: infosRaw.domains, paths: infosRaw.paths, links: infosRaw.links, linkObj: linkObj})

                await fetchClicks()
            } catch (err) {
                console.error('Error fetching initial data:', err)
                setLoading(false)
            }
        }
        fetchData()
    }, [])

    const pathOverview = infos.paths.map(path => {
        const domain = infos.domains.find(d => d.id === path.domain_id)

        const tracked = clicksPerPath.find(c => c.path_id === path.id)?.clicks || 0
        const untracked = badClicksPerPath.find(c => c.path_id === path.id)?.clicks || 0
        const total = tracked + untracked

        const pathTransactions = transactions
            .filter(t => t.pathId === path.id)
            .reduce(
                (acc, t) => {
                    acc.totalTransactions += t.totalTransactions
                    acc.totalCommission += t.totalCommission
                    return acc
                },
                { totalTransactions: 0, totalCommission: 0 }
            )

        const impressions = impressionsPerPath.find(i => i.path_id === path.id)?.impressions || 0


        return {
            domain: domain?.domain || 'Unknown',
            path: path.path,
            tracked,
            untracked,
            total,
            totalTransactions: pathTransactions.totalTransactions,
            totalCommission: pathTransactions.totalCommission,
            impressions
        }
    })
    const filteredPathOverview = pathOverview.filter(row => row.total > 0 || row.tracked > 0 || row.impressions > 0)

    const domainOverview = infos.domains.map(domain => {
        const pathsOfDomain = infos.paths.filter(p => p.domain_id === domain.id)

        const tracked = pathsOfDomain.reduce(
            (sum, p) => sum + (clicksPerPath.find(c => c.path_id === p.id)?.clicks || 0),
            0
        )

        const untracked = pathsOfDomain.reduce(
            (sum, p) => sum + (badClicksPerPath.find(c => c.path_id === p.id)?.clicks || 0),
            0
        )

        const domainTransactions = transactions
            .filter(t => pathsOfDomain.some(p => p.id === t.pathId))
            .reduce(
                (acc, t) => {
                    acc.totalTransactions += t.totalTransactions
                    acc.totalCommission += t.totalCommission
                    return acc
                },
                { totalTransactions: 0, totalCommission: 0 }
            )

        const impressions = pathsOfDomain.reduce(
            (sum, p) => sum + (impressionsPerPath.find(i => i.path_id === p.id)?.impressions || 0),
            0
        )

        return {
            domain: domain.domain,
            tracked,
            untracked,
            total: tracked + untracked,
            totalTransactions: domainTransactions.totalTransactions,
            totalCommission: domainTransactions.totalCommission,
            impressions
        }
    })
    const filteredDomainOverview = domainOverview.filter(d => d.total > 0 || d.tracked > 0 || d.impressions > 0)

    // const facts = useMemo<FactRow[]>(() => {
    //     return infos.paths.map(path => {
    //         const domain = infos.domains.find(d => d.id === path.domain_id)

    //         const tracked = clicksPerPath.find(c => c.path_id === path.id)?.clicks || 0
    //         const untracked = badClicksPerPath.find(c => c.path_id === path.id)?.clicks || 0
    //         const impressions = impressionsPerPath.find(i => i.path_id === path.id)?.impressions || 0

    //         const pathTransactions = transactions.filter(t => t.pathId === path.id)

    //         return {
    //         domainId: domain?.id ?? 0,
    //         domain: domain?.domain ?? 'Unknown',

    //         pathId: path.id,
    //         path: path.path,

    //         clicksTracked: tracked,
    //         clicksUntracked: untracked,
    //         impressions,

    //         transactions: pathTransactions.reduce((s, t) => s + t.totalTransactions, 0),
    //         commission: pathTransactions.reduce((s, t) => s + t.totalCommission, 0)
    //         }
    //     })
    //     }, [
    //     infos,
    //     clicksPerPath,
    //     badClicksPerPath,
    //     impressionsPerPath,
    //     transactions
    //     ])


    return (
        <Container fluid>
            <h3 className="mb-4">Overview</h3>

            <Form
                onSubmit={e => {
                    e.preventDefault()
                    fetchClicks(fromDate, toDate)
                }}
            >
                <Row className="align-items-end mb-3">
                    <Col xs={12} sm={4}>
                        <Form.Group controlId="fromDate">
                            <Form.Label>From date</Form.Label>
                            <Form.Control
                                type="date"
                                value={fromDate}
                                max={toDate}
                                required
                                onChange={e => setFromDate(e.target.value)}
                            />
                        </Form.Group>
                    </Col>

                    <Col xs={12} sm={4}>
                        <Form.Group controlId="toDate">
                            <Form.Label>To date</Form.Label>
                            <Form.Control
                                type="date"
                                value={toDate}
                                max={new Date().toISOString().substring(0, 10)}
                                required
                                onChange={e => setToDate(e.target.value)}
                            />
                        </Form.Group>
                    </Col>

                    <Col xs={12} sm={4}>
                        <Button type="submit" className="mt-2" disabled={loading}>
                            {loading ? <Spinner animation="border" size="sm" /> : 'Submit'}
                        </Button>
                    </Col>
                </Row>
            </Form>

            <hr />

            {loading && clicksPerPath.length === 0 ? (
                <Row>
                    <Col className="text-center">
                        <Spinner animation="border" />
                    </Col>
                </Row>
            ) : (
                <>
                    <Row>
                        <Col xs={12}>
                            <ClickDataTable
                                title="Page click report"
                                data={filteredPathOverview}
                                    columns={[
                                        { name: 'Page', selector: row => row.domain + row.path, sortable: true, width: '40%', wrap: true },
                                        { name: 'Page views', selector: row => row.impressions, format: row => formatNumberLocale(row.impressions, 0), sortable: true, width: '120px' },
                                        { name: 'CTR', selector: row => row.impressions > 0 ? row.total / row.impressions : 0, format: row => `${formatNumberLocale(row.impressions > 0 ? (row.total / row.impressions) * 100 : 0, 1)}%`, sortable: true },
                                        { name: 'NP Clicks', selector: row => row.tracked, format: row => formatNumberLocale(row.tracked, 0), sortable: true, id: 'NPClicks', width: '120px' },
                                        { name: 'Total Clicks', selector: row => row.total, format: row => formatNumberLocale(row.total, 0), sortable: true, id: 'Total', width: '120px' },
                                        { name: 'CR', selector: row => row.total > 0 ? row.totalTransactions / row.total : 0, format: row => `${formatNumberLocale(row.total > 0 ? (row.totalTransactions / row.total) * 100 : 0, 2)}%`, sortable: true },
                                        { name: 'Conversions', selector: row => row.totalTransactions, format: row => formatNumberLocale(row.totalTransactions, 0), sortable: true, width:'120px' },
                                        { name: 'Commission', selector: row => row.totalCommission, format: row => formatNumber(row.totalCommission, user?.settings.currency || ''), sortable: true, width:'120px' },
                                        { name: 'EPC', selector: row => row.total > 0 ? row.totalCommission / row.total : 0, format: row => formatNumber(row.total > 0 ? row.totalCommission / row.total : 0, user?.settings.currency || ''), sortable: true },
                                        { name: 'EPV', selector: row => row.impressions > 0 ? row.totalCommission / row.impressions : 0, format: row => formatNumber(row.impressions > 0 ? row.totalCommission / row.impressions : 0,user?.settings.currency || ''), sortable: true, width: '120px'},
                                    ]}
                                id='NPClicks'
                            />
                        </Col>

                       <Col xs={12}>
                            <ClickDataTable
                                title="Link click report"
                                data={[
                                    ...clicksPerLink.map(c => {
                                        const link = infos.links.find(l => l.id === c.link_id);

                                        const linkTransactions = transactions
                                            .filter(t => t.urlId === c.link_id)
                                            .reduce(
                                                (acc, t) => {
                                                    acc.totalTransactions += t.totalTransactions;
                                                    acc.totalCommission += t.totalCommission;
                                                    return acc;
                                                },
                                                { totalTransactions: 0, totalCommission: 0 }
                                            );

                                        return { 
                                            url: link?.target_url || 'Unknown',
                                            NPClicks: c.clicks || 0,
                                            Clicks: 0,
                                            totalTransactions: linkTransactions.totalTransactions,
                                            totalCommission: linkTransactions.totalCommission
                                        };
                                    }),
                                    ...badClicksPerLink.map(c => {
                                        const link = infos.links.find(l => l.id === c.link_id);
                                        return { 
                                            url: link?.target_url || 'Unknown',
                                            NPClicks: 0,
                                            Clicks: c.clicks || 0,
                                            totalTransactions: 0,
                                            totalCommission: 0
                                        };
                                    })
                                ]}
                                columns={[
                                    { name: 'Target URL', selector: row => row.url, sortable: true, wrap:true, width:'40%' },
                                    { name: 'NP Clicks', selector: row => row.NPClicks, format: row => formatNumberLocale(row.NPClicks, 0), sortable: true, id: 'NPClicks' },
                                    { 
                                        name: 'Total Clicks', 
                                        selector: row => (row.NPClicks || 0) + (row.Clicks || 0), 
                                        format: row => formatNumberLocale((row.NPClicks || 0) + (row.Clicks || 0), 0), 
                                        sortable: true 
                                    },
                                    { name: 'Conversions', selector: row => row.totalTransactions, format: row => formatNumberLocale(row.totalTransactions, 0), sortable: true, width:'120px' },
                                    { name: 'Commission', selector: row => row.totalCommission, format: row => formatNumber(row.totalCommission, user?.settings.currency || ''), sortable: true },
                                    {name: 'EPC', selector: row => row.totalCommission / (row.NPClicks || 0), format: row => formatNumber((row.totalCommission / (row.NPClicks || 0)), user?.settings.currency || ''), sortable: true}
                                ]}
                                id='NPClicks'
                            />
                        </Col>



                        <Col xs={12}>
                            <ClickDataTable
                                title="Campaign overview"
                                data={clicksPerCampaign.map(c => {
                                    const publisher = publishers.find(p => p.internalId === c.campaign_id)
                                    const campaignTransactions = transactions
                                        .filter(t => t.publisherInternalId === c.campaign_id)
                                        .reduce(
                                            (acc, t) => {
                                                acc.totalTransactions += t.totalTransactions
                                                acc.totalCommission += t.totalCommission
                                                return acc
                                            },
                                            { totalTransactions: 0, totalCommission: 0 }
                                        )
                                    return {
                                        name: publisher?.name || 'Unknown',
                                        network: publisher?.network || 'Unknown',
                                        publisherName: publisher?.publisherName || 'Unknown',
                                        clicks: c.clicks,
                                        totalTransactions: campaignTransactions.totalTransactions,
                                        totalCommission: campaignTransactions.totalCommission
                                    }
                                })}
                                columns={[
                                    { name: 'Name', selector: row => row.name, sortable: true },
                                    { name: 'Network', selector: row => row.network, sortable: true },
                                    { name: 'Publisher', selector: row => row.publisherName, sortable: true },
                                    { name: 'NP Clicks', selector: row => row.clicks, format: row => formatNumberLocale(row.clicks, 0), sortable: true, id: 'Clicks' },
                                    { name: 'Conversions', selector: row => row.totalTransactions, format: row => formatNumberLocale(row.totalTransactions, 0), sortable: true, width:'120px' },
                                    { name: 'Commission', selector: row => row.totalCommission, format: row => formatNumber(row.totalCommission, user?.settings.currency || ''), sortable: true },
                                    {name: 'EPC', selector: row => row.totalCommission / (row.clicks || 0), format: row => formatNumber((row.totalCommission / (row.clicks || 0)), user?.settings.currency || ''), sortable: true}
                                ]}
                                id='Clicks'
                            />
                        </Col>

                        <Col xs={12}>
                            <ClickDataTable
                                title="Domain overview"
                                data={filteredDomainOverview.map(d => ({
                                    domain: d.domain,
                                    NPClicks: d.tracked || 0,      // Mindst 0
                                    Clicks: d.untracked || 0,      // Mindst 0
                                    totalTransactions: d.totalTransactions || 0,
                                    totalCommission: d.totalCommission || 0,
                                    impressions: d.impressions || 0

                                }))}
                                columns={[
                                    { name: 'Domain', selector: row => row.domain, sortable: true },
                                    { name: 'Page views', selector: row => row.impressions, format: row => formatNumberLocale(row.impressions, 0), sortable: true },
                                    { name: 'CTR', selector: row => row.impressions > 0 ? row.Clicks / row.impressions : 0, format: row => `${formatNumberLocale(row.impressions > 0 ? (row.Clicks / row.impressions) * 100 : 0, 1)}%`, sortable: true },
                                    { name: 'NP Clicks', id: 'NPClicks', selector: row => row.NPClicks, format: row => formatNumberLocale(row.NPClicks, 0), sortable: true, },
                                    { 
                                        name: 'Total Clicks', 
                                        selector: row => (row.NPClicks || 0) + (row.Clicks || 0), 
                                        format: row => formatNumberLocale((row.NPClicks || 0) + (row.Clicks || 0), 0), 
                                        sortable: true, 
                                        id:'Total'
                                    },
                                    { name: 'CR', selector: row => row.Clicks > 0 ? row.totalTransactions / row.Clicks : 0, format: row => `${formatNumberLocale(row.Clicks > 0 ? (row.totalTransactions / row.Clicks) * 100 : 0, 2)}%`, sortable: true },
                                    { name: 'Conversions', selector: row => row.totalTransactions, format: row => formatNumberLocale(row.totalTransactions, 0), sortable: true, width:'120px' },
                                    { name: 'Commission', selector: row => row.totalCommission, format: row => formatNumber(row.totalCommission, user?.settings.currency || ''), sortable: true },
                                    {name: 'EPC', selector: row => row.totalCommission / (row.NPClicks || 0), format: row => formatNumber((row.totalCommission / (row.NPClicks || 0)), user?.settings.currency || ''), sortable: true},
                                    {name: 'EPV', selector: row => row.totalCommission / (row.impressions || 0), format: row => formatNumber((row.totalCommission / (row.impressions || 0)), user?.settings.currency || ''), sortable: true}
                                ]}
                                id='NPClicks'
                                height="200px"
                            />
                        </Col>

                    </Row>
                </>
            )}
        </Container>
    )
}

export default LinkScriptDashboard
